import React, { useState, useEffect, useContext } from 'react';
import {
  Container, Box, Typography, Grid, Button,
  Chip, CircularProgress, Alert, Divider, Paper, Avatar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import {
  Stars as StarsIcon,
  CheckCircle as CheckIcon,
  AutoAwesome as SparkleIcon,
  ShoppingCart as CartIcon,
  TrendingUp as TrendingUpIcon,
  Search as SearchIcon,
  Gavel as GavelIcon,
  Chat as ChatIcon,
  ArrowUpward as PurchaseIcon,
  ArrowDownward as UsageIcon,
  Receipt as ReceiptIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import AuthContext from '../contexts/AuthContext';

interface CreditPackage {
  id: string;
  name: string;
  credits: number;
  priceEur: number;
}

interface CreditTransaction {
  id: string;
  type: 'PURCHASE' | 'USAGE';
  amount: number;
  balanceAfter: number;
  feature: string | null;
  packageName: string | null;
  pricePaid: number | null;
  createdAt: string;
}

interface StripeInvoice {
  id: string;
  number: string | null;
  status: string | null;
  amountPaid: number;
  currency: string;
  created: string;
  pdfUrl: string | null;
  hostedUrl: string | null;
  description: string | null;
}

const featureLabel: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  'booky-search': { label: 'BOOKY Search', icon: <SearchIcon sx={{ fontSize: 16 }} />, color: '#6366F1' },
  'booky-tribunale': { label: 'BOOKY Tribunale', icon: <GavelIcon sx={{ fontSize: 16 }} />, color: '#8B5CF6' },
  'chatbot': { label: 'Chatbot AI', icon: <ChatIcon sx={{ fontSize: 16 }} />, color: '#06B6D4' },
};

const FEATURE_COSTS = [
  { icon: <SearchIcon />, label: 'BOOKY Search', cost: 1, color: '#6366F1', bg: 'rgba(99,102,241,0.08)' },
  { icon: <GavelIcon />, label: 'BOOKY Tribunale', cost: 3, color: '#8B5CF6', bg: 'rgba(139,92,246,0.08)' },
  { icon: <ChatIcon />, label: 'Chatbot AI', cost: 1, color: '#06B6D4', bg: 'rgba(6,182,212,0.08)' },
];

const CreditsPage: React.FC = () => {
  const { token } = useContext(AuthContext);
  const [searchParams] = useSearchParams();

  const [balance, setBalance] = useState<number>(0);
  const [packages, setPackages] = useState<CreditPackage[]>([]);
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [invoices, setInvoices] = useState<StripeInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const success = searchParams.get('success');
    const cancelled = searchParams.get('cancelled');
    const sessionId = searchParams.get('session_id');

    if (success === 'true' && sessionId) {
      // Chiama verify-session come fallback nel caso il webhook non sia arrivato
      axios.post('/api/credits/verify-session',
        { sessionId },
        { headers: { Authorization: `Bearer ${token}` } }
      ).then(r => {
        if (r.data.credited) {
          setSuccessMessage(`Pagamento completato! +${r.data.creditsAdded} crediti aggiunti. Controlla la tua email per la fattura.`);
        } else if (r.data.alreadyProcessed) {
          setSuccessMessage(`Pagamento confermato! Saldo attuale: ${r.data.currentBalance} crediti.`);
        } else {
          setSuccessMessage('Pagamento completato! I crediti sono stati aggiunti al tuo account.');
        }
        loadData();
      }).catch(() => {
        // Anche se verify fallisce, mostriamo messaggio positivo e ricarichiamo
        setSuccessMessage('Pagamento completato! I crediti sono stati aggiunti al tuo account.');
        setTimeout(() => loadData(), 2000);
      });
    } else if (cancelled === 'true') {
      setErrorMessage('Pagamento annullato. Nessun addebito è stato effettuato.');
    }
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [balanceRes, packagesRes, historyRes, invoicesRes] = await Promise.all([
        axios.get('/api/credits/balance', { headers }),
        axios.get('/api/credits/packages'),
        axios.get('/api/credits/history', { headers }),
        axios.get('/api/credits/invoices', { headers }),
      ]);
      setBalance(balanceRes.data.credits);
      setPackages(packagesRes.data.packages);
      setTransactions(historyRes.data.transactions);
      setInvoices(invoicesRes.data.invoices || []);
    } catch (error) {
      console.error('Errore caricamento crediti:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBuy = async (packageId: string) => {
    setCheckoutLoading(packageId);
    setErrorMessage('');
    try {
      const response = await axios.post('/api/credits/checkout',
        { packageId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = response.data.url;
    } catch (error: any) {
      setErrorMessage(error.response?.data?.message || 'Errore durante il pagamento. Riprova.');
      setCheckoutLoading(null);
    }
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });

  const isPopular = (pkg: CreditPackage) => pkg.credits === 100;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 5 }}>

      {/* Alerts */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Alert>
      )}

      {/* ─── HERO: Saldo + costo per funzione ─── */}
      <Box sx={{
        borderRadius: 4,
        background: 'linear-gradient(135deg, #1B2A4A 0%, #2d3f6b 60%, #3d5078 100%)',
        color: 'white',
        p: { xs: 3, md: 5 },
        mb: 5,
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { md: 'center' },
        gap: 4,
      }}>
        {/* Saldo */}
        <Box sx={{ flex: '0 0 auto', textAlign: { xs: 'center', md: 'left' } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, justifyContent: { xs: 'center', md: 'flex-start' } }}>
            <StarsIcon sx={{ color: '#FFD700', fontSize: 28 }} />
            <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.6)', letterSpacing: 2, fontSize: 11 }}>
              IL TUO SALDO
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography sx={{ fontSize: { xs: 64, md: 80 }, fontWeight: 900, lineHeight: 1, color: '#FFD700' }}>
              {balance}
            </Typography>
            <Typography sx={{ fontSize: 20, fontWeight: 600, color: 'rgba(255,255,255,0.7)', mb: 1 }}>
              crediti
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mt: 0.5 }}>
            I crediti non scadono mai
          </Typography>
        </Box>

        <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.15)', display: { xs: 'none', md: 'block' } }} />
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)', display: { xs: 'block', md: 'none' } }} />

        {/* Costo per funzione */}
        <Box sx={{ flex: 1 }}>
          <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.6)', letterSpacing: 2, fontSize: 11, display: 'block', mb: 2 }}>
            QUANTO COSTA OGNI FUNZIONE
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {FEATURE_COSTS.map((f) => (
              <Box key={f.label} sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 2, px: 2.5, py: 1.5,
                border: '1px solid rgba(255,255,255,0.08)',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ bgcolor: f.bg, color: f.color, width: 34, height: 34 }}>
                    {f.icon}
                  </Avatar>
                  <Typography fontWeight={600} fontSize={14}>{f.label}</Typography>
                </Box>
                <Chip
                  label={`${f.cost} credito${f.cost > 1 ? 'i' : ''} / uso`}
                  size="small"
                  sx={{ bgcolor: f.color, color: 'white', fontWeight: 700, fontSize: 12 }}
                />
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ─── PACCHETTI ─── */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={800} sx={{ fontFamily: 'Syne, sans-serif' }}>
          Acquista crediti
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Scegli il pacchetto più adatto. Il pagamento è sicuro tramite Stripe — fattura automatica via email.
        </Typography>
      </Box>

      <Grid container spacing={3} sx={{ mb: 6 }}>
        {packages.map((pkg) => {
          const highlight = isPopular(pkg);
          const pricePerCredit = (pkg.priceEur / pkg.credits).toFixed(2);
          return (
            <Grid item xs={12} sm={6} md={3} key={pkg.id}>
              <Box sx={{
                borderRadius: 3,
                border: highlight ? '2px solid #6366F1' : '1px solid rgba(0,0,0,0.1)',
                bgcolor: highlight ? 'rgba(99,102,241,0.04)' : 'white',
                boxShadow: highlight ? '0 8px 32px rgba(99,102,241,0.18)' : '0 2px 8px rgba(0,0,0,0.06)',
                position: 'relative',
                transition: 'transform 0.2s, box-shadow 0.2s',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: highlight ? '0 16px 40px rgba(99,102,241,0.25)' : '0 8px 24px rgba(0,0,0,0.12)',
                }
              }}>
                {highlight && (
                  <Chip
                    label="⭐ Più popolare"
                    size="small"
                    icon={<SparkleIcon sx={{ fontSize: '14px !important' }} />}
                    sx={{
                      position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)',
                      bgcolor: '#6366F1', color: 'white', fontWeight: 700, px: 1,
                    }}
                  />
                )}

                <Box sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  {/* Nome pacchetto */}
                  <Typography variant="overline" sx={{ color: highlight ? '#6366F1' : 'text.secondary', fontWeight: 700, letterSpacing: 1.5, fontSize: 11 }}>
                    {pkg.name}
                  </Typography>

                  {/* Crediti — la cosa principale */}
                  <Box sx={{ mt: 1, mb: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                      <Typography sx={{ fontSize: 52, fontWeight: 900, lineHeight: 1, color: highlight ? '#6366F1' : '#1B2A4A' }}>
                        {pkg.credits}
                      </Typography>
                      <Box>
                        <StarsIcon sx={{ color: '#FFD700', fontSize: 20, display: 'block' }} />
                        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ fontSize: 12 }}>
                          crediti
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Prezzo */}
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 0.5 }}>
                    <Typography sx={{ fontSize: 32, fontWeight: 800, color: '#1B2A4A' }}>
                      €{pkg.priceEur}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      una tantum
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 3, display: 'block', fontWeight: 500 }}>
                    €{pricePerCredit} per credito
                  </Typography>

                  {/* Cosa ci fai */}
                  <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckIcon sx={{ fontSize: 16, color: '#10B981' }} />
                      <Typography variant="body2" color="text.secondary">
                        <b style={{ color: '#1B2A4A' }}>{pkg.credits}</b> ricerche BOOKY Search
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckIcon sx={{ fontSize: 16, color: '#10B981' }} />
                      <Typography variant="body2" color="text.secondary">
                        <b style={{ color: '#1B2A4A' }}>{Math.floor(pkg.credits / 3)}</b> analisi BOOKY Tribunale
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckIcon sx={{ fontSize: 16, color: '#10B981' }} />
                      <Typography variant="body2" color="text.secondary">Fattura PDF automatica</Typography>
                    </Box>
                  </Box>

                  <Button
                    variant={highlight ? 'contained' : 'outlined'}
                    fullWidth
                    size="large"
                    startIcon={
                      checkoutLoading === pkg.id
                        ? <CircularProgress size={18} color="inherit" />
                        : <CartIcon />
                    }
                    disabled={checkoutLoading !== null}
                    onClick={() => handleBuy(pkg.id)}
                    sx={{
                      mt: 'auto',
                      borderRadius: 2,
                      fontWeight: 700,
                      textTransform: 'none',
                      fontSize: 15,
                      py: 1.5,
                      ...(highlight ? {
                        bgcolor: '#6366F1',
                        '&:hover': { bgcolor: '#4F46E5' },
                      } : {
                        borderColor: '#6366F1',
                        color: '#6366F1',
                        '&:hover': { borderColor: '#4F46E5', bgcolor: 'rgba(99,102,241,0.05)' },
                      }),
                    }}
                  >
                    {checkoutLoading === pkg.id ? 'Reindirizzamento...' : `Compra a €${pkg.priceEur}`}
                  </Button>
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>

      {/* ─── STORICO ─── */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={800} sx={{ fontFamily: 'Syne, sans-serif' }}>
          Storico movimenti
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Tutti gli acquisti e utilizzi dei tuoi crediti
        </Typography>
      </Box>

      {transactions.length === 0 ? (
        <Paper elevation={0} sx={{
          p: 5, textAlign: 'center',
          border: '1px dashed rgba(0,0,0,0.15)', borderRadius: 3,
          bgcolor: 'rgba(99,102,241,0.02)'
        }}>
          <TrendingUpIcon sx={{ fontSize: 48, color: 'rgba(0,0,0,0.15)', mb: 1 }} />
          <Typography color="text.secondary" fontWeight={500}>
            Nessun movimento ancora
          </Typography>
          <Typography variant="body2" color="text.disabled">
            Acquista il tuo primo pacchetto per iniziare
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 3, overflow: 'hidden' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'rgba(99,102,241,0.04)' }}>
                <TableCell sx={{ fontWeight: 700, color: 'text.secondary', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Data</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.secondary', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Tipo</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.secondary', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Dettaglio</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Crediti</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Saldo dopo</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Importo €</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.map((tx, idx) => {
                const isPurchase = tx.type === 'PURCHASE';
                const fInfo = tx.feature ? featureLabel[tx.feature] : null;
                return (
                  <TableRow
                    key={tx.id}
                    sx={{
                      bgcolor: idx % 2 === 0 ? 'white' : 'rgba(0,0,0,0.015)',
                      '&:hover': { bgcolor: 'rgba(99,102,241,0.04)' },
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" fontSize={13}>
                        {formatDate(tx.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        icon={isPurchase ? <PurchaseIcon sx={{ fontSize: '14px !important' }} /> : <UsageIcon sx={{ fontSize: '14px !important' }} />}
                        label={isPurchase ? 'Acquisto' : 'Utilizzo'}
                        sx={{
                          bgcolor: isPurchase ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.08)',
                          color: isPurchase ? '#059669' : '#DC2626',
                          fontWeight: 700, fontSize: 12,
                          '& .MuiChip-icon': { color: 'inherit' }
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {isPurchase ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <StarsIcon sx={{ fontSize: 16, color: '#FFD700' }} />
                          <Typography variant="body2" fontWeight={600}>
                            {tx.packageName || 'Pacchetto crediti'}
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {fInfo && (
                            <Avatar sx={{ bgcolor: 'rgba(99,102,241,0.1)', color: fInfo.color, width: 24, height: 24, fontSize: 14 }}>
                              {fInfo.icon}
                            </Avatar>
                          )}
                          <Typography variant="body2" fontWeight={600}>
                            {fInfo?.label || tx.feature || '—'}
                          </Typography>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        fontWeight={800}
                        fontSize={15}
                        color={isPurchase ? '#059669' : '#DC2626'}
                      >
                        {isPurchase ? '+' : ''}{tx.amount}
                        <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 0.5, fontWeight: 400 }}>
                          cr.
                        </Typography>
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={`${tx.balanceAfter} cr.`}
                        size="small"
                        sx={{ bgcolor: 'rgba(99,102,241,0.08)', color: '#6366F1', fontWeight: 700, fontSize: 12 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {tx.pricePaid ? (
                        <Typography fontWeight={700} color="text.primary" fontSize={14}>
                          €{tx.pricePaid.toFixed(2)}
                        </Typography>
                      ) : (
                        <Typography color="text.disabled" fontSize={13}>—</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ─── FATTURE ─── */}
      <Box sx={{ mb: 2, mt: 5 }}>
        <Typography variant="h5" fontWeight={800} sx={{ fontFamily: 'Syne, sans-serif', display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReceiptIcon sx={{ color: '#6366F1' }} /> Le tue fatture
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Scarica le fatture PDF dei tuoi acquisti direttamente da Stripe
        </Typography>
      </Box>

      {invoices.length === 0 ? (
        <Paper elevation={0} sx={{
          p: 4, textAlign: 'center',
          border: '1px dashed rgba(0,0,0,0.15)', borderRadius: 3,
          bgcolor: 'rgba(99,102,241,0.02)', mb: 5
        }}>
          <ReceiptIcon sx={{ fontSize: 40, color: 'rgba(0,0,0,0.15)', mb: 1 }} />
          <Typography color="text.secondary" fontWeight={500}>Nessuna fattura ancora</Typography>
          <Typography variant="body2" color="text.disabled">Le fatture appariranno qui dopo il primo acquisto</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 3, overflow: 'hidden', mb: 5 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'rgba(99,102,241,0.04)' }}>
                <TableCell sx={{ fontWeight: 700, color: 'text.secondary', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }}>N° Fattura</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.secondary', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Data</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'text.secondary', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Descrizione</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Importo</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Stato</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoices.map((inv, idx) => (
                <TableRow
                  key={inv.id}
                  sx={{
                    bgcolor: idx % 2 === 0 ? 'white' : 'rgba(0,0,0,0.015)',
                    '&:hover': { bgcolor: 'rgba(99,102,241,0.04)' },
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={700} sx={{ fontFamily: 'monospace', color: '#6366F1' }}>
                      {inv.number || inv.id.slice(0, 12) + '...'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" fontSize={13}>
                      {new Date(inv.created).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {inv.description || 'Acquisto crediti ITF Plus'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight={800} fontSize={15}>
                      €{inv.amountPaid.toFixed(2)}
                      <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 0.5 }}>
                        {inv.currency}
                      </Typography>
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={inv.status === 'paid' ? 'Pagata' : inv.status || '—'}
                      size="small"
                      sx={{
                        bgcolor: inv.status === 'paid' ? 'rgba(16,185,129,0.1)' : 'rgba(245,158,11,0.1)',
                        color: inv.status === 'paid' ? '#059669' : '#D97706',
                        fontWeight: 700, fontSize: 12,
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      {inv.pdfUrl && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
                          href={inv.pdfUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            borderRadius: 1.5, textTransform: 'none', fontWeight: 700,
                            fontSize: 12, py: 0.5, px: 1.5,
                            borderColor: '#6366F1', color: '#6366F1',
                            '&:hover': { borderColor: '#4F46E5', bgcolor: 'rgba(99,102,241,0.05)' }
                          }}
                        >
                          PDF
                        </Button>
                      )}
                      {inv.hostedUrl && (
                        <Button
                          size="small"
                          variant="text"
                          startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                          href={inv.hostedUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            borderRadius: 1.5, textTransform: 'none', fontWeight: 600,
                            fontSize: 12, py: 0.5, px: 1.5, color: 'text.secondary',
                            '&:hover': { color: '#6366F1', bgcolor: 'rgba(99,102,241,0.05)' }
                          }}
                        >
                          Visualizza
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Note legali */}
      <Box sx={{ mt: 4, p: 3, bgcolor: 'rgba(99,102,241,0.04)', borderRadius: 2, border: '1px solid rgba(99,102,241,0.1)' }}>
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.8 }}>
          🔒 Pagamento sicuro gestito da Stripe &nbsp;·&nbsp;
          📄 Fattura PDF inviata automaticamente via email &nbsp;·&nbsp;
          ♾️ I crediti non hanno scadenza e non sono rimborsabili &nbsp;·&nbsp;
          📩 Assistenza: <a href="mailto:info@itfplus.it" style={{ color: '#6366F1', fontWeight: 600 }}>info@itfplus.it</a>
        </Typography>
      </Box>

    </Container>
  );
};

export default CreditsPage;
